// src/components/pages/Dashboard.js
import React from 'react';
import { TrendingUp, Eye, BarChart, Globe, Calendar } from 'lucide-react';

const Dashboard = ({ priceAnalyzer, oiMonitor, dailyTracker, alertManager, settingsManager, showToast, onPageChange }) => {
  const handleQuickAction = (action, pageId) => {
    showToast('info', 'Navigation', `Switching to ${action}`);
    // Actually change the page
    if (onPageChange) {
      onPageChange(pageId);
    }
  };

  const getRecentActivities = () => {
    if (!alertManager.alerts || alertManager.alerts.length === 0) {
      return [];
    }
    
    return alertManager.alerts.slice(0, 5).map(alert => ({
      type: alert.type === 'error' ? 'error' : alert.type === 'warning' ? 'warning' : 'success',
      icon: alert.type === 'error' ? 'fas fa-exclamation-circle' : 
            alert.type === 'warning' ? 'fas fa-exclamation-triangle' : 'fas fa-info-circle',
      title: `${alert.source}: ${alert.symbol}`,
      time: new Date(alert.timestamp).toLocaleString()
    }));
  };

  return (
    <div className="page-section">
      <div className="welcome-banner">
        <div className="welcome-content">
          <h2>Welcome to Binance Analytics Pro</h2>
          <p>Your comprehensive platform for professional cryptocurrency trading analysis</p>
          <div className="quick-actions">
            <button className="action-card" onClick={() => handleQuickAction('Price Analysis', 'price-analysis')}>
              <TrendingUp size={24} />
              <div>
                <h3>Price Analysis</h3>
                <p>Analyze daily highs and lows</p>
              </div>
            </button>
            <button className="action-card" onClick={() => handleQuickAction('OI Monitoring', 'oi-monitoring')}>
              <Eye size={24} />
              <div>
                <h3>OI Monitoring</h3>
                <p>Real-time open interest tracking</p>
              </div>
            </button>
            <button className="action-card" onClick={() => handleQuickAction('Daily Tracker', 'daily-tracker')}>
              <Calendar size={24} />
              <div>
                <h3>Daily Tracker</h3>
                <p>Multi-day percentage changes</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <h3>System Status</h3>
            <div className="status-indicator">
              <div className="status-dot online"></div>
              <span>Online</span>
            </div>
          </div>
          <div className="card-content">
            <div className="status-grid">
              <div className="status-item">
                <span>API Connection</span>
                <span className="status-good">Connected</span>
              </div>
              <div className="status-item">
                <span>Data Feed</span>
                <span className="status-good">Active</span>
              </div>
              <div className="status-item">
                <span>Discord Alerts</span>
                <span className={settingsManager.settings?.discordWebhook ? 'status-good' : 'status-warning'}>
                  {settingsManager.settings?.discordWebhook ? 'Configured' : 'Not Configured'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h3>Analytics Summary</h3>
            <BarChart size={18} />
          </div>
          <div className="card-content">
            <div className="summary-stats">
              <div className="summary-item">
                <div className="summary-value">{priceAnalyzer.analysisCount || 0}</div>
                <div className="summary-label">Total Analyses</div>
              </div>
              <div className="summary-item">
                <div className="summary-value">{oiMonitor.getActiveMonitorCount ? oiMonitor.getActiveMonitorCount() : 0}</div>
                <div className="summary-label">Active Monitors</div>
              </div>
              <div className="summary-item">
                <div className="summary-value">{alertManager.getTotalAlertsCount ? alertManager.getTotalAlertsCount() : 0}</div>
                <div className="summary-label">Total Alerts</div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h3>Daily Analytics</h3>
            <Calendar size={18} />
          </div>
          <div className="card-content">
            <div className="status-grid">
              <div className="status-item">
                <span>Symbols Tracked</span>
                <span>{dailyTracker.marketData?.length || '--'}</span>
              </div>
              <div className="status-item">
                <span>Status</span>
                <span className={dailyTracker.isLoading ? 'status-warning' : 'status-good'}>
                  {dailyTracker.isLoading ? 'Loading' : 'Ready'}
                </span>
              </div>
              <div className="status-item">
                <span>Days Analyzed</span>
                <span>{dailyTracker.config?.daysToFetch || '--'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h3>Market Overview</h3>
            <Globe size={18} />
          </div>
          <div className="card-content">
            <div className="status-grid">
              <div className="status-item">
                <span>Total OI</span>
                <span>--</span>
              </div>
              <div className="status-item">
                <span>24h Change</span>
                <span>--</span>
              </div>
              <div className="status-item">
                <span>Active Pairs</span>
                <span>{priceAnalyzer.symbols?.length || '--'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card wide">
          <div className="card-header">
            <h3>Recent Activity</h3>
            <button className="card-action" onClick={() => onPageChange && onPageChange('settings')}>
              Settings
            </button>
          </div>
          <div className="card-content">
            <div className="activity-list">
              {getRecentActivities().length === 0 ? (
                <div className="activity-empty">No recent activity</div>
              ) : (
                getRecentActivities().map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className={`activity-icon ${activity.type}`}>
                      <i className={activity.icon}></i>
                    </div>
                    <div className="activity-content">
                      <div className="activity-title">{activity.title}</div>
                      <div className="activity-time">{activity.time}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;